/** NI (Northern Ireland) regulatory submission pathways — MDR devices and
 *  IVDR in-vitro diagnostics. NI follows EU MDR 2017/745 and IVDR 2017/746
 *  under the Windsor Framework.
 *
 *  ## Scope — MDR devices + IVDR diagnostics
 *
 *  - NI MDR Classes IIa / IIb / III (the implantable IIb variant is handled
 *    by the backend's `normalize_device_class`).
 *  - NI IVDR Classes A_sterile / B / C / D — in-vitro diagnostics under
 *    EU IVDR 2017/746 as applied in NI; routed to the `uk_ni_ivdr` engine.
 *  - NO drugs or biologics (EU pathway handles those).
 *
 *  ## Marking
 *
 *  Northern Ireland devices must bear either:
 *    - CE marking — when a UK Approved Body performed the conformity assessment,
 *      the device bears CE marking only (recognised in NI under the Windsor Framework).
 *    - CE-UKNI marking — when a UK Notified Body (UKCA scheme) performed the
 *      assessment. Devices with CE-UKNI marking may ONLY be placed on the NI
 *      market, not the EU market.
 *
 *  Conformity-assessment requirements mirror EU MDR (Annex IX/X/XI) since
 *  NI operates under EU MDR 2017/745 per the Windsor Framework.
 *
 *  ## Backend mapping
 *
 *  The `ni-class-iia`/`ni-class-iib`/`ni-class-iii` values map to canonical MDR
 *  classes (IIa/IIb/III), and the `ivdr-class-*` values to canonical IVDR
 *  classes (A/A_sterile/B/C/D), via `app/dossier/uk_ni/dispatch_policy`.
 */

import type { Product } from '@/features/product/types';
import type { SubmissionGroup } from './us-submission-pathways';

export const NI_SUBMISSION_PATHWAYS: SubmissionGroup[] = [
  {
    productType: 'device',
    label: 'Northern Ireland — MDR 2017/745 (Windsor Framework)',
    options: [
      {
        value: 'ni-class-iia',
        label: 'MDR Class IIa — NB technical-doc sample + QMS audit (CE / CE-UKNI)',
      },
      {
        value: 'ni-class-iib',
        label: 'MDR Class IIb — NB broader tech-doc review + QMS (CE / CE-UKNI)',
      },
      {
        value: 'ni-class-iii',
        label: 'MDR Class III — full QMS + tech-doc review + SSCP (CE / CE-UKNI)',
      },
    ],
  },
  {
    productType: 'device',
    label: 'Northern Ireland — IVDR 2017/746 (CE-UKNI · Windsor Framework)',
    options: [
      { value: 'ivdr-class-a-sterile', label: 'IVDR Class A sterile — NB for sterility (CE-UKNI)' },
      { value: 'ivdr-class-b',         label: 'IVDR Class B — NB QMS audit + tech-doc sample (CE-UKNI)' },
      { value: 'ivdr-class-c',         label: 'IVDR Class C — NB QMS + tech-doc review + SSP (CE-UKNI)' },
      { value: 'ivdr-class-d',         label: 'IVDR Class D — most stringent + EURL batch verification (CE-UKNI)' },
    ],
  },
];

/** Pre-fill the picker with the most-common pathway per product type.
 *  NI dossier scope is device-only (MDR IIa/IIb/III); non-device types fall
 *  back to Class IIa (the lowest in-scope class) as a safe default.
 *  Drug/biologic '' signals no NI pathway — the dispatch form should gate on
 *  a valid selection before enabling Generate. */
export const NI_DEFAULT_BY_TYPE: Record<Product['product_type'], string> = {
  device: 'ni-class-iia',
  drug: 'ni-class-iia',
  biologic: 'ni-class-iia',
  combination: 'ni-class-iia',
};
